import React from 'react';
import './PolicyStyles.css'; // Import the external CSS file

const PrivacyPolicy = () => {
  return (
    <div className="policy-container"> {/* Apply the main container class */}
      <h1>Privacy Policy for Medhas AI</h1>

      <p><em>Last updated: June 10, 2026</em></p>

      <p>Medhas AI, LLC (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy and is committed to
protecting the personal data of our users (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;). This Privacy Policy explains
how we collect, use, disclose, and safeguard your information when you visit our website
(https://medhas.ai), utilize our artificial intelligence platform, or interact with our services
(collectively, the &quot;Services&quot;).</p>

<p>By accessing or using our Services, you acknowledge that you have read and understood this
Privacy Policy. If you do not agree with the practices described herein, please discontinue your
use of our website and platform immediately.</p>

      <h2>1.Information We Collect</h2>

      <p>We collect information that identifies, relates to, describes, or could reasonably be linked to you.
The categories of personal data we collect depend on how you interact with our platform:</p>

      <h4>Account Registration Data:</h4>
      <p>When you create an account directly, or authenticate via third-
party providers (including Google, Facebook, and LinkedIn), we collect your name, email
address, profile picture link, and basic account identifiers provided by those platforms.</p>
      {/* <ul>
        <li>Email address</li>
        <li>First name and last name</li>
        <li>Profile picture (if provided by your social login provider)</li>
        <li>Unique identifiers associated with your social login accounts (e.g., Firebase User ID, Google User ID, Facebook User ID)</li>
      </ul> */}

      <h4>User Content and Conversational Inputs:</h4>
      <p>We collect and store the text, documents,
images, audio, and database queries you upload into our Retrieval-Augmented Generation
(RAG) workspaces and chatbot interface (&quot;User Content&quot;).</p>
       
       <h4>Optional Communications Data:</h4>
       <p>Data: If you voluntarily choose to submit your phone number in
our optional signup field, we collect it to send transactional, security, or promotional
marketing messages via our communication infrastructure.</p>

<h4>Automatically Collected Technical Data:</h4>
<p>When you navigate our platform, our
infrastructure partners (including cloud hosting, caching, and web security platforms)
automatically collect your IP address, browser type, operating system, device identifiers,
device location (generalized), and platform usage metrics (e.g., tokens consumed,
timestamps).</p>

      <h2>2.How We Use Your Information</h2>
      <p>We process your personal data for the following commercial and operational purposes:</p>
      <ul>
        <li>
          To operate, maintain, host, secure, and deliver the Medhas AI platform.
        </li>
        <li>
          To manage your account dashboard, subscriptions, and process payments.
        </li>
        <li>
          To run your private RAG workspaces, knowledge bases, and team collaboration features.
        </li>
        <li>
          To communicate with you regarding security alerts, multi-factor authentication, service
updates, and—where you have provided affirmative consent—promotional or marketing
campaigns.
        </li>
        <li><strong>For Model Optimization:</strong> As outlined in Section 5 of our Terms of Service, we may utilize
anonymized or de-identified prompt inputs and model interactions to fine-tune, retrain, and
optimize our underlying artificial intelligence models to improve output accuracy and user
experience.</li>
      </ul>

      <h2>3.Data Sharing and Third-Party Processors</h2>
      <p>We do not sell, rent, or trade your personal data to third-party data brokers or advertisers. To
deliver our self-hosted platform, we share specific data categories with trusted infrastructure
processors under strict confidentiality mandates:</p>
      <ul>
        <li><strong>Cloud Infrastructure Providers:</strong> Your account data and User Content are hosted and
processed on secure cloud computing networks (such as Amazon Web Services) within the
United States.</li>
        <li><strong>Web Security and Delivery Frameworks:</strong> Technical traffic data is processed through
security and optimization applications (such as Cloudflare) to mitigate cyber-attacks and
automated scraping.</li>
        <li><strong>Communication Platforms:</strong> Email and SMS routing frameworks are used to deliver
verification codes, system tickets, and voluntary marketing messages.</li>
        <li><strong>Payment Gateways:</strong> Credit card numbers and subscription billing details are processed
exclusively by PCI-compliant third-party financial institutions. We do not store raw credit card
details on our servers.</li>
      </ul>
      <p>Notwithstanding anything to the contrary in this Privacy Policy, text messaging originator opt-in
data and consent are strictly excluded from all data sharing frameworks. Mobile information and
SMS consent will not be shared, sold, or rented to any third parties or affiliates for marketing or
promotional purposes. </p>

      <h2>4.New Jersey Data Privacy Act (NJDPA) Rights</h2>
      <p>Because Medhas AI, LLC operates out of New Jersey, we recognize and honor the specific
statutory rights granted to New Jersey consumers under state privacy laws. You have the right
to request:</p>
    <ul>
      <li><strong>The Right to Access:</strong> Confirmation as to whether we are processing your personal data
and a portable copy of the specific data we hold.</li>
 <li><strong>The Right to Correct:</strong> Revisions or fixes to inaccurate or incomplete personal information
associated with your account.</li>
<li><strong>The Right to Delete:</strong> The deletion of your account data and conversational histories from
our cloud databases, subject to standard technical backups and legal preservation
exemptions.</li>
<li><strong>The Right to Opt-Out of AI Training:</strong> The right to restrict the use of your conversational
data for automated profiling or machine learning model retraining.</li>
    </ul>
      <p>To exercise any of these rights, you or your authorized agent may submit a manual request to
contact@medhas.ai. We will validate your identity and respond to your request within forty-five
(45) days in accordance with state regulations.</p>

      <h2>5.Data Security and AWS Retention</h2>
      <p>We implement commercially reasonable technical, physical, and administrative security
configurations designed to protect your data within our cloud environment. This includes utilizing
advanced data encryption protocols for data in transit and data at rest on our cloud servers.
However, no method of transmission over the internet or cloud storage network is entirely
infallible. We retain your data only for as long as your account remains active or as required to
fulfill the commercial purposes outlined in this policy. If you terminate your account, your
conversational histories and uploaded knowledge bases will be programmatically queued for
deletion from our active infrastructure.</p>


      <h2>6.Children&#39;s Privacy</h2>
      <p>Our platform is strictly intended for individuals who are at least 18 years of age. Medhas AI, LLC
does not knowingly collect, scrape, or maintain personal data from children under the age of 13.
If we discover that an individual under the required legal age has bypassed our eligibility gate
and created an account, we will purge their data from our databases immediately.</p>

      <h2>7.Cookies and Tracking Mechanisms</h2>
      <p>We utilize essential operational cookies, web beacons, and local storage tokens to keep you
securely logged into your account, remember your system preferences (such as dark mode or
sidebar layout), and track total token expenditures. You can manage or disable cookies through
your individual browser preferences, though doing so may break core platform functionalities.</p>

      <h2>8.Changes to this Privacy Policy</h2>
      <p>We reserve the right to revise this Privacy Policy periodically to reflect infrastructure
modifications, model fine-tuning changes, or updated statutory laws. When adjustments are
published, we will modify the &quot;Last Updated&quot; date at the top of this page and post a visible
notice inside the platform application dashboard or dispatch a notification to your registered
email.</p>


      <h2>9.Contact Our Privacy Team</h2>
      <p>For questions, regulatory grievances, or to formally submit a data deletion or AI retraining opt-
out request, please contact us at:</p>
  <div className="contact-details-privacy">
      <p><strong>Medhas AI, LLC</strong></p>
  <p>Attn: Data Privacy Compliance</p>
  <p>
    <strong>Email:</strong>
    <a href="mailto:contact@medhas.ai">
      contact@medhas.ai
    </a>
  </p>
  <p>
    <strong>Website:</strong>
    <a
      href="https://medhas.ai"
      target="_blank"
      rel="noopener noreferrer"
    >
      medhas.ai
    </a>
  </p>
  <p>
    <strong>Mailing Address:</strong><br />
    2230 Route 70 W, STE 2<br />
    Cherry Hill, NJ 08002<br />
    United States
  </p>
  </div>
     
      <div className="auth-back-button">
          <button onClick={() => window.location.href = 'https://medhas.ai/'} className="back-button">Back</button>
          {/* <button onClick={() => window.location.href = 'https://medhas.ai/signup'} className="signup-button">Join Now</button> */}
        </div>
    </div>
  );
};

export default PrivacyPolicy;